import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from "react-redux";
import { calculateFactorScores } from './calculations';
import styled,{keyframes} from 'styled-components';
import { Icon } from 'semantic-ui-react';
import factor_content from './factor_content.json';
import OptionCard from "new_components/organisms/OptionCard";
import SelectCard from "new_components/organisms/SelectCard";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import typography from "styles/typography";
import Text from "new_components/atoms/Text";

const checkContent = (factor) => {
  if (factor_content[factor]) {
    return factor_content[factor];
  }
  return "";
};

const BenchmarkDiagnostic = ({ onFactorSelected, goBack, data }) => {
  const [sortedDifferenceData, setSortedDifferenceData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFactor, setSelectedFactor] = useState(null);

  const core_data = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );

  useEffect(() => {
    if (core_data && data) {
      const chartData = calculateFactorScores(data, core_data);

      const differenceSeries = chartData.series.find(
        (seriesItem) => seriesItem.name === "Difference"
      );

      if (differenceSeries) {
        const differenceData = differenceSeries.data.map(
          (dataPoint, index) => ({
            name: dataPoint.x,
            value:
              dataPoint.y != null
                ? parseFloat((dataPoint.y * 10).toFixed(1))
                : null, // Scale to base 100
            factorId: dataPoint.factorId || index, // Ensure factorId is defined
            dimensionId: dataPoint.dimensionId,
          })
        );

        const sortedData = differenceData
          .filter((item) => item.value != null)
          .sort((a, b) => a.value - b.value); // Smallest to largest

        setSortedDifferenceData(sortedData);
      }
    }
  }, [core_data, data]);

  const factorsPerPage = 3;
  const totalPages = sortedDifferenceData
    ? Math.ceil(sortedDifferenceData.length / factorsPerPage)
    : 0;

  const nextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
    setSelectedFactor(null); // Reset selected factor when changing pages
  };

  const prevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    setSelectedFactor(null); // Reset selected factor when changing pages
  };

  const handleSelect = (factor) => {
    setSelectedFactor(factor);
  };

  const selectFactor = (factor) => {
    if (onFactorSelected) {
      onFactorSelected(factor);
    }
  };

  if (!sortedDifferenceData) {
    return (
      <Container>
        <Title>Global Benchmark Comparison Diagnostic</Title>
        <Description>Loading data...</Description>
      </Container>
    );
  }

  const indexOfLastFactor = currentPage * factorsPerPage;
  const indexOfFirstFactor = indexOfLastFactor - factorsPerPage;
  const currentFactors = sortedDifferenceData.slice(
    indexOfFirstFactor,
    indexOfLastFactor
  );

  return (
    <Container>
      <BackButton onClick={goBack}>
        <Icon name="chevron left" /> Back
      </BackButton>
      <Title>Select a Factor to Create an Action Plan</Title>
      <Description>
        These scores represent the differences from the global benchmark
        average. Positive values indicate better performance compared to the
        benchmark, while negative values indicate areas for improvement.
      </Description>

      <CardArea key={currentPage}>
        {currentFactors.map((factor) => (
          <FactorCard
            key={`${factor.factorId}-${currentPage}`} // Ensure unique key
            factor={factor}
            maxScore={Math.max(
              ...sortedDifferenceData.map((item) => item.value)
            )}
            onClick={() => handleSelect(factor)}
            description={checkContent(factor.name)?.description}
            selected={
              selectedFactor &&
              selectedFactor.factorId === factor.factorId &&
              selectedFactor.dimensionId === factor.dimensionId
            }
          />
        ))}
      </CardArea>

      <Pagination>
        <Icon
          name="chevron left"
          onClick={prevPage}
          style={{
            cursor: currentPage === 1 ? "default" : "pointer",
            opacity: currentPage === 1 ? 0.5 : 1,
          }}
        />
        <PageInfo>
          Page {currentPage} of {totalPages}
        </PageInfo>
        <Icon
          name="chevron right"
          onClick={nextPage}
          style={{
            cursor: currentPage === totalPages ? "default" : "pointer",
            opacity: currentPage === totalPages ? 0.5 : 1,
          }}
        />
      </Pagination>

      {selectedFactor ? (
        <SelectedAreaComponent
          factor={selectedFactor}
          selectFactor={selectFactor}
        />
      ) : (
        <T3>Select a factor above to view more details and continue</T3>
      )}
    </Container>
  );
};

export default BenchmarkDiagnostic;

// FactorCard Component
const FactorCard = ({ factor, onClick, selected, description }) => {
  return (
    <SelectCard
      onClick={onClick}
      selected={selected}
      title={factor.name}
      footerText="Difference from global benchmark scores"
      description={description}
      smallCard
      noFooter
      customFooter={
        <div style={{ paddingBottom: 10 }}>
          <ScoreText typographyStyle={typography.body.semibold}>
            {factor.value}%
          </ScoreText>
        </div>
      }
      iconName={ICON_SET.layers}
      score={factor.value > 0 ? `+${factor.value}%` : `${factor.value}%`}
    />
  );

  return (
    <OptionCard
      onClick={onClick}
      subtext="Difference from global benchmark scores"
      selected={selected}
      option={factor}
      description={description}
      score={factor.value > 0 ? `+${factor.value}` : `${factor.value}`}
    />
  );

  return (
    <Card onClick={onClick} selected={selected}>
      <CardTitle>{factor.name}</CardTitle>
      <CardDescription>Difference from global benchmark scores</CardDescription>

      <Score>
        {factor.value > 0 ? `+${factor.value}%` : `${factor.value}%`}
      </Score>

      <CardDescription>{description}</CardDescription>
    </Card>
  );
};

// SelectedAreaComponent
const SelectedAreaComponent = ({ factor, selectFactor }) => {
  const factorContent = checkContent(factor.name);

  return (
    <SelectedArea>
      <SelectedTitle>Selected Factor: {factor.name}</SelectedTitle>
      <SelectedDescription>{factorContent?.description}</SelectedDescription>

      <T2>Difference from global benchmark scores</T2>

      <CurrentScore>
        {factor.value > 0 ? `+${factor.value}%` : `${factor.value}%`}
      </CurrentScore>
      <ScoreOverview>
        <OverviewDescription>
          This score indicates your organization's current performance in{" "}
          <strong>{factor.name}</strong> relative to global averages.
        </OverviewDescription>
      </ScoreOverview>

      <T2>Key Impact Areas:</T2>
      {factorContent.keyImpactAreas.map((area, index) => (
        <li>{area}</li>
      ))}

      <SmallButton onClick={() => selectFactor(factor)}>
        Continue with {factor.name}
      </SmallButton>
    </SelectedArea>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ScoreText = styled(Text)`
  font-size: 24px;
  font-weight: bold;
  margin-right: 8px;
`;

// Styled Components
const Container = styled.div`
  padding: 20px;
  font-family: 'Raleway', sans-serif;
  width: 100%;

   opacity:0;
    animation: ${fadeIn} 1s ease-out forwards;
`;

const BackButton = styled.div`
  color: #2D70E2;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 15px;
`;

const BlueTitle = styled.div`
  color: #2D70E2;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 15px;
`;

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #666;
  margin-bottom: 20px;
  max-width:700px;
`;

const CardArea = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
`;

const PageInfo = styled.span`
  margin: 0 10px;
  margin-top: 5px;
  font-size: 14px;
`;

const Card = styled.div`
  padding: 20px;
  border: ${(props) => (props.selected ? '2px solid #2A3039' : '1px solid #DFDFDF')};
  font-family: 'Raleway', sans-serif;
  border-radius: 8px;
  cursor: pointer;
  flex: 1;
  min-width: 200px; 

  &:hover {
    box-shadow: 0px 0px 10px rgba(0,0,0,0.1);
  }
`;

const CardTitle = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #2A3039;
  margin-bottom: 5px;
`;

const CardDescription = styled.div`
  font-size: 12px;
  color: #666D79;
`;

const Score = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const Bar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #DFDFDF;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const BarFill = styled.div`
  width: ${(props) => props.width}%;
  height: 100%;
  background-color: ${(props) => props.color};
`;

const SelectedArea = styled.div`
  background-color: #F8FAFF;
  padding: 20px;
  border-radius: 8px;
  color: #2A3039;
  margin-top: 20px;
`;

const SelectedTitle = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
`;

const SelectedDescription = styled.div`
  margin-bottom: 20px;
  font-size: 14px;
`;

const T2 = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ScoreOverview = styled.div`
  margin-bottom: 20px;
`;

const CurrentScore = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const OverviewDescription = styled.div`
  font-size: 14px;
  margin-top: 10px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #DFDFDF;
  border-radius: 10px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: ${(props) => props.width}%;
  height: 100%;
  background-color: #2D70E2;
`;

const SmallButton = styled.div`
  padding: 6px 12px;
  background-color: #2D70E2;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  transition: background-color 0.3s;
  display: inline-block;
  margin-top: 20px;
  border: none;

  &:hover {
    background-color: #1c4db8;
  }
`;

const T3 = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2A3039;
  margin-top: 40px;
  width: 100%;
  text-align: center;
`;
